import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Music, MapPin, Calendar, Check } from 'lucide-react';
import axios from 'axios';
import RSVPBackgroundLayer from './RSVPBackgroundLayer';
import PreviewModeNotice from './PreviewModeNotice';
import { getRsvpButtonShadowStyle } from '../../../lib/utils/buttonShadow';
import { resolveRsvpLabels } from '../../../lib/utils/rsvpLabels';
import {
  isBackgroundLayerActive,
  isSurfaceVisible,
  getRsvpBackgroundLayerStyle,
  getRsvpSurfaceStyle,
  getBgLayerWidthPercent,
  getSurfaceWidthPercent,
  getSurfaceAlpha,
  getBgLayerAlpha,
  getContentWidthStyle,
  getBgLayerRadiusPx,
  getSurfaceRadiusPx,
} from '../../../lib/utils/responseCardStyle';

const API = process.env.REACT_APP_BACKEND_URL;
const DIETARY_OPTIONS = ['None', 'Vegan', 'Vegetarian', 'Gluten-free', 'Lactose-free', 'Other'];

// ─── Step Definitions ─────────────────────────────────────
// STEP 0: intro scroll trigger (handled by intersection)
// STEP 1: RSVP block (name, attend, count, phone)
// STEP 2: transition "You're in."
// STEP 3: guests (names + dietary) — only if count > 1
// STEP 4: music suggestion
// STEP 5: message (optional, calm)
// STEP 6: final "See you there."
// STEP_NO: decline path

export default function RsvpImmersive({ section, data, theme, settings, rsvpRef }) {
  const cs = data.customStyle || {};
  const labels = resolveRsvpLabels(data.labels);
  const bg = data.background;

  const accentColor = cs.accentColor || theme.accent;
  const textColor = cs.textColor || theme.textPrimary;
  const bgColor = cs.bgColor || theme.surface || theme.bg;
  const headingFont = cs.fontFamily || theme.headingFont;
  const bodyFont = theme.bodyFont;
  const buttonBg = cs.buttonColor || theme.buttonBg || accentColor;
  const buttonText = cs.buttonTextColor || theme.buttonText || '#ffffff';

  // Phase RSVP Style 3.0 — three real layers. No inner card duplication.
  const bgActive = isBackgroundLayerActive(cs);
  const surfaceVisible = isSurfaceVisible(cs);
  const bgWidthPct = getBgLayerWidthPercent(cs);
  const surfaceWidthPct = getSurfaceWidthPercent(cs);
  const surfaceAlpha = getSurfaceAlpha(cs);
  // Section-level artwork opacity tied to Background opacity when bgActive.
  const sectionImageAlpha = bgActive ? getBgLayerAlpha(cs) : 1;
  const contentWidthStyle = getContentWidthStyle(cs);

  // Background layer style (outer block, opt-in).
  // Width: 100% = section content width; we strip section horizontal padding
  // when active so bgWidthPercent=100 reaches the viewport edges.
  const bgWrapperStyle = bgActive ? {
    width: `${bgWidthPct}%`,
    borderRadius: getBgLayerRadiusPx(cs) !== null ? `${getBgLayerRadiusPx(cs)}px` : '1.5rem',
    overflow: 'hidden',
    ...getRsvpBackgroundLayerStyle(cs, { backgroundColor: bgColor, borderColor: undefined }),
  } : null;

  // Surface material — applied to the ONE Surface div (no second wrapper).
  const surfaceMaterial = surfaceVisible
    ? getRsvpSurfaceStyle(cs, { backgroundColor: bgColor, borderColor: `${textColor}1f` })
    : {};
  // Surface width: when bgActive → % of bg-layer parent; else → variant max-w-md.
  const surfaceWidthStyle = bgActive
    ? { width: `${surfaceWidthPct}%`, maxWidth: '100%' }
    : (surfaceWidthPct < 100 ? { width: `${surfaceWidthPct}%`, maxWidth: '100%' } : null);

  const eventDate = data.eventDate || settings.eventDate || '';
  const venueName = data.venueName || settings.venueName || '';
  const showEventInfo = data.showEventInfo !== false;

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    attending: null,
    guest_count: 1,
    dietary: 'None',
    otherDietary: '',
  });
  const [guests, setGuests] = useState([]);
  const [expandedGuest, setExpandedGuest] = useState(null);
  const [song, setSong] = useState('');
  const [message, setMessage] = useState('');
  const [declineNote, setDeclineNote] = useState('');
  const [showNudge, setShowNudge] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [declineNoteSent, setDeclineNoteSent] = useState(false);
  const [resolved, setResolved] = useState(null);       // null | 'attending' | 'declined'
  const [overlayPhase, setOverlayPhase] = useState('visible'); // 'visible' | 'fading' | 'gone'
  const [submitError, setSubmitError] = useState('');
  const containerRef = useRef(null);

  // ─── Helpers ──────────────────────────────
  const scrollToSelf = () => {
    setTimeout(() => containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const triggerCompletion = (type) => {
    setResolved(type);
    setOverlayPhase('visible');
    setTimeout(() => setOverlayPhase('fading'), 1500);
    setTimeout(() => setOverlayPhase('gone'), 2400);
  };

  const initGuests = (count) => {
    const arr = [];
    for (let i = 0; i < count - 1; i++) {
      arr.push({ name: '', dietary: 'None', otherDietary: '' });
    }
    setGuests(arr);
  };

  // ─── Submit to Backend ────────────────────
  const submitRSVP = async (extras = {}) => {
    // Phase 2.2 Lite — review mode: don't actually POST to the backend.
    // Keep the UI flow visually intact (so the client can see the success
    // screen) but skip the network call entirely.
    if (settings?.disableRsvpSubmit) {
      setSubmitting(false);
      return true;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const primaryDietary = form.dietary === 'None' ? undefined : (form.dietary === 'Other' ? form.otherDietary : form.dietary);
      const payload = {
        name: form.name,
        attending: form.attending,
        guest_count: form.guest_count,
        phone: form.phone || undefined,
        dietary: primaryDietary,
        additional_guests: guests.length > 0 ? guests.map(g => ({
          name: g.name || undefined,
          dietary: g.dietary === 'None' ? undefined : (g.dietary === 'Other' ? g.otherDietary : g.dietary),
        })) : undefined,
        song_suggestion: extras.song || undefined,
        message: extras.message || undefined,
      };
      await axios.post(`${API}/api/public/invite/${settings.slug}/rsvp`, payload);
      return true;
    } catch (err) {
      setSubmitError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Handlers ─────────────────────────────
  const handleConfirm = async () => {
    if (!form.name.trim() || form.attending === null) return;
    if (form.attending) {
      initGuests(form.guest_count);
      setStep(2); // transition
      scrollToSelf();
      setTimeout(() => {
        if (form.guest_count > 1) {
          setStep(3); // guests
        } else {
          setStep(4); // music
        }
        scrollToSelf();
      }, 2500);
    } else {
      setStep('no');
      scrollToSelf();
    }
  };

  const handleGuestsContinue = () => {
    const hasEmptyNames = guests.some(g => !g.name.trim());
    if (hasEmptyNames && !showNudge) {
      setShowNudge(true);
      return;
    }
    setShowNudge(false);
    setStep(4);
    scrollToSelf();
  };

  const handleSongAdd = () => {
    setStep(5); // message
    scrollToSelf();
  };

  const handleSongSkip = () => {
    setStep(5); // message
    scrollToSelf();
  };

  const handleMessageSkip = async () => {
    const ok = await submitRSVP({ song: song.trim(), message: '' });
    if (!ok) return;
    triggerCompletion('attending');
    scrollToSelf();
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    const ok = await submitRSVP({ song: song.trim(), message: message.trim() });
    if (!ok) return;
    setMessageSent(true);
    setTimeout(() => {
      setMessageSent(false);
      triggerCompletion('attending');
      scrollToSelf();
    }, 900);
  };

  const handleDeclineSkip = async () => {
    const ok = await submitRSVP({ message: '' });
    if (!ok) return;
    triggerCompletion('declined');
    scrollToSelf();
  };

  const handleDeclineSend = async () => {
    if (!declineNote.trim()) return;
    const ok = await submitRSVP({ message: declineNote.trim() });
    if (!ok) return;
    setDeclineNoteSent(true);
    setTimeout(() => {
      setDeclineNoteSent(false);
      triggerCompletion('declined');
      scrollToSelf();
    }, 900);
  };

  // ─── Shared Styles ───────────────────────
  const inputStyle = {
    borderColor: `${textColor}15`,
    color: textColor,
    background: 'transparent',
    fontFamily: bodyFont,
  };

  const fadeVariants = {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -8 },
  };

  // ─── Container width: 'full' (edge-to-edge bleed) | 'inset' (centered card) ───
  const containerWidth = data.containerWidth === 'inset' ? 'inset' : 'full';
  const isInset = containerWidth === 'inset';
  // Inset customization (only used when isInset)
  const insetRadius = typeof data.insetRadius === 'number' ? data.insetRadius : 28;          // 0-48 px
  const insetMargin = typeof data.insetMargin === 'number' ? data.insetMargin : 16;          // 0-48 px (horizontal)

  // ═══════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════
  // 3-Layer Honest Tree:
  //   <section .rsvp-section>
  //     <BackgroundLayer target="section" />
  //     {bgActive ? <BgLayer><SurfaceLayer><FormLayer/></SurfaceLayer></BgLayer>
  //              : <SurfaceLayer><FormLayer/></SurfaceLayer>}
  //   </section>
  // Surface radius: explicit slider wins; otherwise inset → insetRadius, full → 0.
  const surfaceRadiusPx = getSurfaceRadiusPx(cs);
  const surfaceLayerStyle = {
    ...(surfaceWidthStyle || {}),
    ...(bgActive ? {} : { maxWidth: '28rem' }),
    ...(surfaceVisible ? {
      ...surfaceMaterial,
      overflow: 'hidden',
      border: surfaceMaterial.borderColor ? '1px solid' : undefined,
      borderRadius: surfaceRadiusPx !== null
        ? `${surfaceRadiusPx}px`
        : (isInset ? `${insetRadius}px` : 0),
    } : { background: 'transparent' }),
  };
  return (
    <section
      ref={(el) => { containerRef.current = el; if (rsvpRef) rsvpRef.current = el; }}
      className={`${bgActive ? 'py-10' : (isInset ? 'py-10' : 'px-5 py-24')} rsvp-bg-host`}
      style={{
        background: 'transparent',
        ...(isInset && !bgActive ? { paddingLeft: `${insetMargin}px`, paddingRight: `${insetMargin}px` } : {}),
      }}
      data-testid="rsvp-immersive-section"
    >
      <RSVPBackgroundLayer background={bg} target="section" surfaceAlpha={sectionImageAlpha} />
      {(() => {
      const surfaceJsx = (
      <div
        className="mx-auto relative rsvp-bg-host"
        style={surfaceLayerStyle}
        data-testid="rsvp-surface-layer"
      >
        {surfaceVisible && (
          <RSVPBackgroundLayer background={bg} target="card" surfaceAlpha={surfaceAlpha} />
        )}
        <div
          className={`relative z-10 mx-auto rsvp-bg-content ${isInset ? 'px-6 py-14' : ''}`}
          style={contentWidthStyle || undefined}
          data-testid="rsvp-form-layer"
        >

        <PreviewModeNotice visible={!!settings?.disableRsvpSubmit} />

        {/* ─── INTERACTIVE FLOW (steps 0–5) ─── */}
        {resolved === null && (
          <AnimatePresence mode="wait">

          {/* ─── STEP 0 & 1: RSVP BLOCK ─── */}
          {(step === 0 || step === 1) && (
            <motion.div
              key="rsvp-block"
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.4 }}
            >
              {/* Intro text */}
              <p
                className="text-center text-sm tracking-wide mb-10 opacity-60"
                style={{ color: textColor, fontFamily: bodyFont }}
                data-testid="rsvp-intro-text"
              >
                {labels.heading || 'Will you be there?'}
              </p>

              {/* Name — with expandable dietary */}
              <div className="mb-6">
                <label className="block text-[11px] font-medium tracking-wide mb-2" style={{ color: textColor, opacity: 0.5, fontFamily: bodyFont }}>
                  {labels.nameLabel || "What's your name?"}
                </label>
                <div className="rounded-xl border overflow-hidden transition-all" style={{ borderColor: expandedGuest === 'primary-step1' ? accentColor : `${textColor}0d` }}>
                  <div className="flex items-center">
                    <input
                      type="text"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder={labels.namePlaceholder || 'Your name'}
                      className="flex-1 px-4 py-3 text-sm outline-none bg-transparent"
                      style={{ color: textColor, fontFamily: bodyFont }}
                      data-testid="rsvp-immersive-name"
                    />
                    <button
                      type="button"
                      onClick={() => setExpandedGuest(expandedGuest === 'primary-step1' ? null : 'primary-step1')}
                      className="px-3 py-3 shrink-0"
                      data-testid="rsvp-primary-dietary-toggle"
                    >
                      <ChevronDown
                        className="w-4 h-4 transition-transform"
                        style={{ color: textColor, opacity: 0.3, transform: expandedGuest === 'primary-step1' ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      />
                    </button>
                  </div>

                  {expandedGuest === 'primary-step1' && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="px-4 pb-3 border-t"
                      style={{ borderColor: `${textColor}10` }}
                    >
                      <label className="block text-[10px] tracking-wide mt-2 mb-1.5 opacity-40" style={{ color: textColor }}>
                        {labels.dietaryLabel || 'Dietary'}
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {DIETARY_OPTIONS.map(opt => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => setForm(f => ({ ...f, dietary: opt }))}
                            className="px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-all"
                            style={{
                              borderColor: form.dietary === opt ? accentColor : `${textColor}15`,
                              background: form.dietary === opt ? accentColor : 'transparent',
                              color: form.dietary === opt ? buttonText : textColor,
                            }}
                            data-testid={`rsvp-primary-dietary-${opt.toLowerCase()}`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                      {form.dietary === 'Other' && (
                        <input
                          type="text"
                          value={form.otherDietary}
                          onChange={e => setForm(f => ({ ...f, otherDietary: e.target.value }))}
                          placeholder="Please specify"
                          className="w-full mt-2 px-3 py-2 rounded-lg border text-xs outline-none"
                          style={{ borderColor: `${textColor}0d`, color: textColor, background: 'transparent', fontFamily: bodyFont }}
                          data-testid="rsvp-primary-dietary-other"
                        />
                      )}
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Attend Yes/No */}
              <div className="mb-6">
                <label className="block text-[11px] font-medium tracking-wide mb-3" style={{ color: textColor, opacity: 0.5, fontFamily: bodyFont }}>
                  {labels.attendLabel || 'Will you attend?'}
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, attending: true }))}
                    className="flex-1 py-3 rounded-xl border text-sm font-medium transition-all"
                    style={{
                      borderColor: form.attending === true ? accentColor : `${textColor}15`,
                      background: form.attending === true ? accentColor : 'transparent',
                      color: form.attending === true ? buttonText : textColor,
                      fontFamily: bodyFont,
                    }}
                    data-testid="rsvp-immersive-yes"
                  >
                    {labels.yesLabel || 'Yes'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, attending: false }))}
                    className="flex-1 py-3 rounded-xl border text-sm font-medium transition-all"
                    style={{
                      borderColor: form.attending === false ? accentColor : `${textColor}15`,
                      background: form.attending === false ? accentColor : 'transparent',
                      color: form.attending === false ? buttonText : textColor,
                      fontFamily: bodyFont,
                    }}
                    data-testid="rsvp-immersive-no"
                  >
                    {labels.noLabel || 'No'}
                  </button>
                </div>
              </div>

              {/* Guest Count — only if attending */}
              {form.attending && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.3 }}
                  className="mb-6 overflow-hidden"
                >
                  <label className="block text-[11px] font-medium tracking-wide mb-3" style={{ color: textColor, opacity: 0.5, fontFamily: bodyFont }}>
                    {labels.guestCountLabel || 'How many will attend?'}
                  </label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, guest_count: n }))}
                        className="flex-1 py-3 rounded-xl border text-sm font-medium transition-all"
                        style={{
                          borderColor: form.guest_count === n ? accentColor : `${textColor}15`,
                          background: form.guest_count === n ? accentColor : 'transparent',
                          color: form.guest_count === n ? buttonText : textColor,
                          fontFamily: bodyFont,
                        }}
                        data-testid={`rsvp-immersive-count-${n}`}
                      >
                        {n === 4 ? '4+' : n}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Phone — optional, only if attending */}
              {form.attending && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  className="mb-8 overflow-hidden"
                >
                  <label className="block text-[11px] font-medium tracking-wide mb-2" style={{ color: textColor, opacity: 0.35, fontFamily: bodyFont }}>
                    {labels.phoneLabel || 'For day-of updates (optional)'}
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="+1 (555) 000-0000"
                    className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all focus:ring-1"
                    style={{ borderColor: `${textColor}0d`, color: textColor, background: 'transparent', fontFamily: bodyFont, '--tw-ring-color': `${accentColor}60` }}
                    data-testid="rsvp-immersive-phone"
                  />
                </motion.div>
              )}

              {/* Confirm CTA */}
              <button
                onClick={handleConfirm}
                disabled={!form.name.trim() || form.attending === null || submitting}
                className="w-full py-3.5 rounded-xl text-sm font-semibold tracking-wide transition-all disabled:opacity-30 rsvp-submit-shadow"
                style={{ background: buttonBg, color: buttonText, fontFamily: bodyFont, ...getRsvpButtonShadowStyle(cs.buttonShadowStrength) }}
                data-testid="rsvp-immersive-confirm"
              >
                {labels.submitLabel || 'Confirm'}
              </button>
            </motion.div>
          )}

          {/* ─── STEP 2: TRANSITION ─── */}
          {step === 2 && (
            <motion.div
              key="transition"
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, y: -8, transition: { duration: 0.5, ease: 'easeIn' } }}
              className="text-center py-16"
              data-testid="rsvp-transition-screen"
            >
              <motion.p
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
                className="text-2xl font-light mb-3"
                style={{ color: textColor, fontFamily: headingFont }}
              >
                {labels.transitionTitle || "You're in."}
              </motion.p>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.5 }}
                transition={{ duration: 0.4, delay: 0.55, ease: 'easeOut' }}
                className="text-sm"
                style={{ color: textColor, fontFamily: bodyFont }}
              >
                {labels.transitionSubtitle || "We can't wait to have you."}
              </motion.p>
            </motion.div>
          )}

          {/* ─── STEP 3: GUESTS (names + dietary) ─── */}
          {step === 3 && (
            <motion.div
              key="guests"
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.4 }}
              data-testid="rsvp-guests-step"
            >
              <p className="text-lg font-light text-center mb-8" style={{ color: textColor, fontFamily: headingFont }}>
                {labels.guestsTitle || "Who's joining you?"}
              </p>

              <div className="space-y-2 mb-8">
                {/* Primary guest (already provided name) */}
                <div className="rounded-xl border px-4 py-3 flex items-center justify-between" style={{ borderColor: `${textColor}15` }}>
                  <span className="text-sm font-medium" style={{ color: textColor, fontFamily: bodyFont }}>{form.name}</span>
                  <span className="text-[10px] tracking-wider opacity-30" style={{ color: textColor }}>You</span>
                </div>

                {/* Additional guests */}
                {guests.map((g, i) => (
                  <div key={i} className="rounded-xl border overflow-hidden transition-all" style={{ borderColor: expandedGuest === i ? accentColor : `${textColor}15` }}>
                    <button
                      type="button"
                      onClick={() => setExpandedGuest(expandedGuest === i ? null : i)}
                      className="w-full px-4 py-3 flex items-center justify-between"
                    >
                      <input
                        type="text"
                        value={g.name}
                        onChange={e => {
                          const next = [...guests];
                          next[i].name = e.target.value;
                          setGuests(next);
                        }}
                        onClick={e => e.stopPropagation()}
                        placeholder="Guest name"
                        className="bg-transparent outline-none text-sm font-medium w-full"
                        style={{ color: textColor, fontFamily: bodyFont }}
                        data-testid={`rsvp-guest-name-${i}`}
                      />
                      <ChevronDown
                        className="w-4 h-4 shrink-0 transition-transform ml-2"
                        style={{ color: textColor, opacity: 0.3, transform: expandedGuest === i ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      />
                    </button>

                    {/* Expanded: dietary */}
                    {expandedGuest === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="px-4 pb-3 border-t"
                        style={{ borderColor: `${textColor}10` }}
                      >
                        <label className="block text-[10px] tracking-wide mt-2 mb-1.5 opacity-40" style={{ color: textColor }}>
                          Dietary
                        </label>
                        <div className="flex flex-wrap gap-1.5">
                          {DIETARY_OPTIONS.map(opt => (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => {
                                const next = [...guests];
                                next[i].dietary = opt;
                                setGuests(next);
                              }}
                              className="px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-all"
                              style={{
                                borderColor: g.dietary === opt ? accentColor : `${textColor}15`,
                                background: g.dietary === opt ? accentColor : 'transparent',
                                color: g.dietary === opt ? buttonText : textColor,
                              }}
                              data-testid={`rsvp-guest-${i}-dietary-${opt.toLowerCase()}`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                        {g.dietary === 'Other' && (
                          <input
                            type="text"
                            value={g.otherDietary}
                            onChange={e => {
                              const next = [...guests];
                              next[i].otherDietary = e.target.value;
                              setGuests(next);
                            }}
                            placeholder="Please specify"
                            className="w-full mt-2 px-3 py-2 rounded-lg border text-xs outline-none"
                            style={{ borderColor: `${textColor}0d`, color: textColor, background: 'transparent', fontFamily: bodyFont }}
                            data-testid={`rsvp-guest-${i}-dietary-other`}
                          />
                        )}
                      </motion.div>
                    )}
                  </div>
                ))}
              </div>

              {/* Soft nudge if names are missing */}
              {showNudge && (
                <motion.p
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xs text-center mb-4 opacity-45"
                  style={{ color: textColor, fontFamily: bodyFont }}
                >
                  {labels.guestsSubtitle || 'Just so we know who to expect.'}
                </motion.p>
              )}

              {showNudge ? (
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowNudge(false); }}
                    className="flex-1 py-3.5 rounded-xl text-sm font-semibold tracking-wide transition-all"
                    style={{ background: buttonBg, color: buttonText, fontFamily: bodyFont }}
                    data-testid="rsvp-guests-add-now"
                  >
                    Add now
                  </button>
                  <button
                    onClick={() => { setShowNudge(false); setStep(4); scrollToSelf(); }}
                    className="flex-1 py-3.5 rounded-xl text-sm transition-all border"
                    style={{ borderColor: `${textColor}15`, color: textColor, opacity: 0.5, fontFamily: bodyFont }}
                    data-testid="rsvp-guests-skip-continue"
                  >
                    Continue
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleGuestsContinue}
                  className="w-full py-3.5 rounded-xl text-sm font-semibold tracking-wide transition-all"
                  style={{ background: buttonBg, color: buttonText, fontFamily: bodyFont }}
                  data-testid="rsvp-guests-continue"
                >
                  Continue
                </button>
              )}
            </motion.div>
          )}

          {/* ─── STEP 4: MUSIC ─── */}
          {step === 4 && (
            <motion.div
              key="music"
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.4 }}
              className="text-center"
              data-testid="rsvp-music-step"
            >
              <Music className="w-5 h-5 mx-auto mb-4 opacity-25" style={{ color: textColor }} />
              <p className="text-lg font-light mb-1" style={{ color: textColor, fontFamily: headingFont }}>
                {labels.musicTitle || 'Before the night begins\u2026'}
              </p>
              <p className="text-sm font-medium mb-1 opacity-70" style={{ color: textColor, fontFamily: bodyFont }}>
                {labels.songLabel || 'Add a song'}
              </p>
              <p className="text-xs opacity-35 mb-8" style={{ color: textColor, fontFamily: bodyFont }}>
                {labels.musicHint || "Something you'd love to hear"}
              </p>

              <input
                type="text"
                value={song}
                onChange={e => setSong(e.target.value)}
                placeholder={labels.songPlaceholder || 'Song name or artist'}
                className="w-full px-4 py-3 rounded-xl border text-sm outline-none text-center transition-all focus:ring-1 mb-6"
                style={{ borderColor: `${textColor}0d`, color: textColor, background: 'transparent', fontFamily: bodyFont, '--tw-ring-color': `${accentColor}60` }}
                data-testid="rsvp-song-input"
              />

              {submitError && (
                <p className="text-xs text-red-500 text-center mb-3" role="alert" data-testid="rsvp-immersive-error-music">
                  {submitError}
                </p>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleSongSkip}
                  disabled={submitting}
                  className="flex-1 py-3 rounded-xl text-sm transition-all border"
                  style={{ borderColor: `${textColor}15`, color: textColor, opacity: 0.5, fontFamily: bodyFont }}
                  data-testid="rsvp-song-skip"
                >
                  Skip
                </button>
                <button
                  onClick={handleSongAdd}
                  disabled={submitting || !song.trim()}
                  className="flex-1 py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-30"
                  style={{ background: buttonBg, color: buttonText, fontFamily: bodyFont }}
                  data-testid="rsvp-song-add"
                >
                  Add
                </button>
              </div>
            </motion.div>
          )}

          {/* ─── STEP 5: MESSAGE (OPTIONAL) ─── */}
          {step === 5 && (
            <motion.div
              key="message-step"
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.4 }}
              className="py-12"
              data-testid="rsvp-message-screen"
            >
              {messageSent ? (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  transition={{ duration: 0.3 }}
                  className="text-center text-sm font-light py-8"
                  style={{ color: textColor, fontFamily: bodyFont }}
                  data-testid="rsvp-message-sent"
                >
                  Sent
                </motion.p>
              ) : (
                <>
                  <p className="text-lg font-light text-center mb-8" style={{ color: textColor, fontFamily: headingFont }}>
                    {labels.messageLabel || 'A note.'}
                  </p>

                  <input
                    type="text"
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder={labels.messagePlaceholder || 'Say something to them\u2026'}
                    className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all focus:ring-1 mb-8"
                    style={{ borderColor: `${textColor}0d`, color: textColor, background: 'transparent', fontFamily: bodyFont, '--tw-ring-color': `${accentColor}40` }}
                    data-testid="rsvp-message-input"
                  />

                  {submitError && (
                    <p className="text-xs text-red-500 text-center mb-3" role="alert" data-testid="rsvp-immersive-error-message">
                      {submitError}
                    </p>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={handleMessageSkip}
                      disabled={submitting}
                      className="flex-1 py-3 rounded-xl text-sm transition-all border"
                      style={{ borderColor: `${textColor}12`, color: textColor, opacity: 0.4, fontFamily: bodyFont }}
                      data-testid="rsvp-message-skip"
                    >
                      Skip
                    </button>
                    <button
                      onClick={handleSendMessage}
                      disabled={submitting || !message.trim()}
                      className="flex-1 py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-30"
                      style={{ background: buttonBg, color: buttonText, fontFamily: bodyFont }}
                      data-testid="rsvp-message-send"
                    >
                      Send
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          )}

          {/* ─── NO PATH: NOTE (OPTIONAL) ─── */}
          {step === 'no' && (
            <motion.div
              key="decline-note"
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.4 }}
              className="py-12"
              data-testid="rsvp-decline-screen"
            >
              {declineNoteSent ? (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  transition={{ duration: 0.3 }}
                  className="text-center text-sm font-light py-8"
                  style={{ color: textColor, fontFamily: bodyFont }}
                  data-testid="rsvp-decline-note-sent"
                >
                  Sent
                </motion.p>
              ) : (
                <>
                  <p className="text-lg font-light text-center mb-8" style={{ color: textColor, fontFamily: headingFont }}>
                    {labels.declineNoteTitle || 'A note, if you\'d like.'}
                  </p>

                  <input
                    type="text"
                    value={declineNote}
                    onChange={e => setDeclineNote(e.target.value)}
                    placeholder={labels.declineNotePlaceholder || 'Something for them\u2026'}
                    className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all focus:ring-1 mb-8"
                    style={{ borderColor: `${textColor}0d`, color: textColor, background: 'transparent', fontFamily: bodyFont, '--tw-ring-color': `${accentColor}40` }}
                    data-testid="rsvp-decline-note"
                  />

                  {submitError && (
                    <p className="text-xs text-red-500 text-center mb-3" role="alert" data-testid="rsvp-immersive-error-decline">
                      {submitError}
                    </p>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={handleDeclineSkip}
                      disabled={submitting}
                      className="flex-1 py-3 rounded-xl text-sm transition-all border"
                      style={{ borderColor: `${textColor}12`, color: textColor, opacity: 0.4, fontFamily: bodyFont }}
                      data-testid="rsvp-decline-skip"
                    >
                      Skip
                    </button>
                    <button
                      onClick={handleDeclineSend}
                      disabled={submitting || !declineNote.trim()}
                      className="flex-1 py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-30"
                      style={{ background: buttonBg, color: buttonText, fontFamily: bodyFont }}
                      data-testid="rsvp-decline-send"
                    >
                      Send
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          )}

          </AnimatePresence>
        )}

        {/* ─── RESOLVED STATE + FADING OVERLAY ─── */}
        {resolved !== null && (
          <div style={{ display: 'grid' }}>
            {/* Base layer: compact resolved state */}
            <div style={{ gridArea: '1/1' }} className="text-center py-8" data-testid="rsvp-resolved-state">
              {resolved === 'attending' ? (
                <>
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Check className="w-4 h-4" style={{ color: accentColor }} />
                    <p className="text-sm font-medium" style={{ color: textColor, fontFamily: bodyFont }}>
                      You're attending
                    </p>
                  </div>
                  {form.guest_count > 1 && (
                    <p className="text-xs opacity-40" style={{ color: textColor, fontFamily: bodyFont }}>
                      {form.guest_count} guests
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm font-medium opacity-50" style={{ color: textColor, fontFamily: bodyFont }}>
                  Not attending
                </p>
              )}
            </div>

            {/* Overlay: the emotional moment, fades away */}
            {overlayPhase !== 'gone' && (
              <motion.div
                style={{ gridArea: '1/1', background: bgColor, zIndex: 1 }}
                animate={{
                  opacity: overlayPhase === 'fading' ? 0 : 1,
                  filter: overlayPhase === 'fading' ? 'blur(3px)' : 'blur(0px)',
                }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="text-center py-20"
                data-testid="rsvp-completion-overlay"
              >
                {resolved === 'attending' ? (
                  <>
                    <p className="text-2xl font-light mb-2" style={{ color: textColor, fontFamily: headingFont }}>
                      {labels.thankYouTitle || 'See you there.'}
                    </p>

                    {showEventInfo && (eventDate || venueName) && (
                      <div className="flex items-center justify-center gap-4 text-xs opacity-35 mt-3" style={{ color: textColor, fontFamily: bodyFont }}>
                        {eventDate && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {eventDate}
                          </span>
                        )}
                        {venueName && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {venueName}
                          </span>
                        )}
                      </div>
                    )}

                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 0.4 }}
                      transition={{ delay: 0.6, duration: 0.5 }}
                      className="text-sm mt-6"
                      style={{ color: textColor, fontFamily: bodyFont }}
                    >
                      {labels.thankYouAccept || "You're all set."}
                    </motion.p>
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-light mb-2" style={{ color: textColor, fontFamily: headingFont }}>
                      {labels.declineTitle || "We'll miss you."}
                    </p>
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 0.4 }}
                      transition={{ delay: 0.6, duration: 0.5 }}
                      className="text-sm mt-4"
                      style={{ color: textColor, fontFamily: bodyFont }}
                    >
                      {labels.declineSubtitle || 'Thank you for letting us know.'}
                    </motion.p>
                  </>
                )}
              </motion.div>
            )}
          </div>
        )}

        </div>
      </div>
      );
      return bgActive ? (
        <div className="mx-auto relative rsvp-bg-host" style={bgWrapperStyle} data-testid="rsvp-background-layer">
          <div className="p-4 sm:p-6">{surfaceJsx}</div>
        </div>
      ) : surfaceJsx;
      })()}
    </section>
  );
}
